
import {
  parseTimeString
} from './helpers/parsers.js'
import { roundTime, differencePlainTime } from './helpers/math.js'
import Duration from './Duration.js'
import Calendar from './Calendar.js'
import { regulateTime, rejectTime } from './helpers/regulate.js'
import { DateTimeFormat, Locale, TimeFormat } from '@neumatter/intl'
import { isObject } from './helpers/is.js'
import { balanceTime } from './helpers/balance.js'
import DateTime from './DateTime.js'
import { toSecondsStringPrecision, formatSecondsStringPart, getOption } from './helpers/utils.js'
import SlotMap from '@neumatter/slot-map'

const SYMBOL_IS_TIME = Symbol.for('neumatter.DateTime.PlainTime.isPlainTime')

const UNITS = new Map([
  ['nanosecond', 0],
  ['microsecond', 1],
  ['millisecond', 2],
  ['second', 3],
  ['minute', 4],
  ['hour', 5],
  ['day', 6],
  ['month', 7],
  ['year', 8],
  ['nanoseconds', 0],
  ['microseconds', 1],
  ['milliseconds', 2],
  ['seconds', 3],
  ['minutes', 4],
  ['hours', 5],
  ['days', 6],
  ['months', 7],
  ['years', 8]
])

const UNITS_ARRAY = [
  'nanosecond',
  'microsecond',
  'millisecond',
  'second',
  'minute',
  'hour',
  'day',
  'month',
  'year'
]

function getISOUnit (name) {
  return UNITS_ARRAY[UNITS.get(name)]
}

export default class PlainTime {
  static now (options = { timeZone: 'UTC', calendar: 'iso8601' }) {
    const now = DateTime.now(options)
    return new PlainTime(
      now.hour,
      now.minute,
      now.second,
      now.millisecond,
      now.microsecond,
      now.nanosecond,
      now.calendar
    )
  }

  static compare (a, b) {
    a = PlainTime.isPlainTime(a) ? a : PlainTime.from(a)
    b = PlainTime.isPlainTime(b) ? b : PlainTime.from(b)

    if (a.hour !== b.hour) {
      return a.hour < b.hour ? -1 : 1
    } else if (a.minute !== b.minute) {
      return a.minute < b.minute ? -1 : 1
    } else if (a.second !== b.second) {
      return a.second < b.second ? -1 : 1
    } else if (a.millisecond !== b.millisecond) {
      return a.millisecond < b.millisecond ? -1 : 1
    } else if (a.microsecond !== b.microsecond) {
      return a.microsecond < b.microsecond ? -1 : 1
    } else if (a.nanosecond !== b.nanosecond) {
      return a.nanosecond < b.nanosecond ? -1 : 1
    }

    return 0
  }

  static isPlainTime (input) {
    return (
      typeof input === 'object' &&
      typeof input[SYMBOL_IS_TIME] === 'boolean' &&
      input[SYMBOL_IS_TIME]
    )
  }

  static from (item) {
    if (typeof item === 'string') {
      item = parseTimeString(item)
    }

    if (isObject(item)) {
      return new PlainTime(
        item.hour ?? 0,
        item.minute ?? 0,
        item.second ?? 0,
        item.millisecond ?? 0,
        item.microsecond ?? 0,
        item.nanosecond ?? 0,
        item.calendar ?? 'iso8601'
      )
    }
  }

  #hour
  #minute
  #second
  #millisecond
  #microsecond
  #nanosecond
  #calendar

  constructor (
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond,
    calendar
  ) {
    rejectTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond
    )

    this.#hour = hour
    this.#minute = minute
    this.#second = second
    this.#millisecond = millisecond
    this.#microsecond = microsecond
    this.#nanosecond = nanosecond
    this.#calendar = Calendar.from(calendar)
  }

  get [SYMBOL_IS_TIME] () {
    return true
  }

  get hour () {
    return this.#hour
  }

  get minute () {
    return this.#minute
  }

  get second () {
    return this.#second
  }

  get millisecond () {
    return this.#millisecond
  }

  get microsecond () {
    return this.#microsecond
  }

  get nanosecond () {
    return this.#nanosecond
  }

  get calendar () {
    return this.#calendar
  }

  add (durationLike) {
    const duration = Duration.from(durationLike)
    let { hour, minute, second, millisecond, microsecond, nanosecond } = this

    hour += duration.hours
    minute += duration.minutes
    second += duration.seconds
    millisecond += duration.milliseconds
    microsecond += duration.microseconds
    nanosecond += duration.nanoseconds

    ;({ hour, minute, second, millisecond, microsecond, nanosecond } = balanceTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond
    ))

    ;({ hour, minute, second, millisecond, microsecond, nanosecond } = regulateTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      'reject'
    ))

    return new PlainTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      this.calendar
    )
  }

  subtract (durationLike) {
    const duration = Duration.from(durationLike).negate()
    let { hour, minute, second, millisecond, microsecond, nanosecond } = this

    hour += duration.hours
    minute += duration.minutes
    second += duration.seconds
    millisecond += duration.milliseconds
    microsecond += duration.microseconds
    nanosecond += duration.nanoseconds

    ;({ hour, minute, second, millisecond, microsecond, nanosecond } = balanceTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond
    ))

    ;({ hour, minute, second, millisecond, microsecond, nanosecond } = regulateTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      'reject'
    ))

    return new PlainTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      this.calendar
    )
  }

  until (isoTimeLike, options) {
    return differencePlainTime('until', this, isoTimeLike, options)
  }

  since (isoTimeLike, options) {
    return differencePlainTime('since', this, isoTimeLike, options)
  }

  round (optionsOrSmallestUnit) {
    if (optionsOrSmallestUnit === undefined) {
      throw new TypeError('options parameter is required')
    }

    const options =
      typeof optionsOrSmallestUnit === 'string'
        ? { smallestUnit: optionsOrSmallestUnit }
        : Object.create(optionsOrSmallestUnit)

    const smallestUnit = getOption(
      options,
      'smallestUnit',
      'string',
      ['hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond'],
      'nanosecond'
    )

    const roundingMode = getOption(
      options,
      'roundingMode',
      'string',
      ['ceil', 'floor', 'expand', 'trunc', 'halfCeil', 'halfFloor', 'halfExpand', 'halfTrunc', 'halfEven'],
      'trunc'
    )

    let roundingIncrement

    switch (smallestUnit) {
      case 'hour':
        roundingIncrement = 24
        break
      case 'minute':
      case 'second':
        roundingIncrement = 60
        break
      case 'millisecond':
      case 'microsecond':
      case 'nanosecond':
        roundingIncrement = 1000
        break
    }

    const { hour, minute, second, millisecond, microsecond, nanosecond } = roundTime(
      this.hour,
      this.minute,
      this.second,
      this.millisecond,
      this.microsecond,
      this.nanosecond,
      roundingIncrement,
      smallestUnit,
      roundingMode
    )

    return new PlainTime(
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      this.calendar
    )
  }

  toDateTime (relativeTo) {
    relativeTo = DateTime.isDateTime(relativeTo)
      ? relativeTo
      : DateTime.from(relativeTo, { timeZone: relativeTo.timeZone })

    return createDateFromTime(relativeTo, this)
  }

  toLocaleString (locales = Locale.systemLocale, options = {}) {
    if (!('calendar' in options) && this.calendar.id !== 'iso8601') {
      options.calendar = this.calendar.id
    }

    return new TimeFormat(locales, options).format(this)
  }

  toString (options) {
    let { hour, minute, second, millisecond, microsecond, nanosecond } = this
    const opts = Object.create(options === undefined ? {} : options)
    const { precision, unit, increment } = toSecondsStringPrecision(opts)

    if ('fractionalDigits' in opts || 'smallestUnit' in opts) {
      const { roundingMode } = options
      ;({ hour, minute, second, millisecond, microsecond, nanosecond } = roundTime(
        hour,
        minute,
        second,
        millisecond,
        microsecond,
        nanosecond,
        increment,
        unit,
        roundingMode
      ))
    }

    const hourString = `00${hour}`.slice(-2)
    const minuteString = `00${minute}`.slice(-2)
    const seconds = formatSecondsStringPart(
      second,
      millisecond,
      microsecond,
      nanosecond,
      precision
    )
    let result = `${hourString}:${minuteString}${seconds}`

    if (this.calendar.id !== 'iso8601') {
      result += `[u-ca=${this.calendar.id}]`
    }

    return result
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }

  inspect () {
    return `PlainTime(${this.toString()})`
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `PlainTime(\x1b[35m${this.toString()}\x1b[0m)`
  }
}

function createDateFromTime (relativeTo, isoTime) {
  const slotMap = SlotMap.get(relativeTo)
  return new DateTime(
    slotMap.get('year'),
    slotMap.get('month'),
    slotMap.get('day'),
    isoTime.hour,
    isoTime.minute,
    isoTime.second,
    isoTime.millisecond,
    isoTime.microsecond,
    isoTime.nanosecond,
    slotMap.get('timeZone'),
    slotMap.get('calendar')
  )
}
